/**
 * Curated font registry for MotionForge templates — LOCAL files.
 *
 * PROBLEM: Templates reference fonts like 'Rubik' in CSS font-family. If nothing
 * actually loads that font, the browser's Remotion Player falls back to one
 * system font while the headless render Chromium falls back to a different one
 * (or times out fetching from a CDN) — different widths → preview and render
 * positions diverge.
 *
 * OLD SOLUTION (did not work): @remotion/google-fonts loaded face files from
 * fonts.gstatic.com at runtime. The render worker's Chromium can't reliably
 * reach the CDN from the sandbox, so it dropped to system fallbacks while the
 * preview loaded the real faces.
 *
 * WHY NOT PLAIN @font-face CSS with local paths?
 * Vite serves the `frontend/public/` folder at URL `/` (so fonts live at
 * `/fonts/rubik/400.woff2`). But Remotion's bundler serves public files at the
 * `/public/...` prefix via its staticHash — so a CSS `url('/fonts/rubik/...')`
 * declaration resolves correctly in preview but 404s in the render sandbox,
 * giving us the same silent-fallback problem we started with.
 *
 * CURRENT SOLUTION: Ship woff2 files as static assets under
 * `frontend/public/fonts/<slug>/<weight>.woff2` and register each face at
 * runtime via the FontFace Web API (new FontFace + document.fonts.add +
 * face.load), using Remotion's `staticFile()` helper to resolve the URL.
 * `staticFile()` returns `/fonts/...` in the browser (where
 * `window.remotion_staticBase` is undefined) and `/public/fonts/...` inside
 * the render bundle (where the bundler sets the static base to `/public`).
 * Same data, path mangled correctly for each context.
 *
 * Why NOT also inject CSS @font-face rules: an earlier version did both.
 * Each face ended up registered TWICE in document.fonts — once `unloaded`
 * (from CSS, never auto-loaded) and once `loaded` (from the JS path). With
 * `font-display: block`, the unloaded sibling kept css resolution blocking
 * forever, and under render concurrency (15+ tabs) the duplicate fetches
 * stalled the bundle's static server, hanging delayRender past 28s. The JS
 * path alone covers everything we need: actively loads every face, blocks
 * frame capture via delayRender, and produces a single FontFace per family.
 *
 * Wrapped in delayRender/continueRender so Remotion's renderer blocks frame
 * capture until every font is loaded — guaranteeing preview and render share
 * identical glyph widths.
 *
 * To add a font:
 *   1. Drop the woff2 into frontend/public/fonts/<slug>/<weight>.woff2
 *      (or re-run scripts/fetch-fonts.sh after editing its FONTS list).
 *   2. Add an entry to FONT_REGISTRY below with matching slug + weights.
 *   3. Add a SupportedFont entry to SUPPORTED_FONTS.
 */

import { delayRender, continueRender, staticFile } from "remotion";

export interface SupportedFont {
  /** Display name shown to users / Claude. */
  name: string;
  /** Exact `font-family` name registered via FontFace below. */
  family: string;
  /**
   * What components should put in CSS `fontFamily`. Includes a fallback chain
   * so that, in the worst case of a load failure, something still renders.
   */
  css: string;
  /** Category — useful for UI grouping and for Claude's font selection. */
  category: "sans" | "display" | "mono" | "serif";
}

/**
 * Per-font format capabilities — derived from FONT_REGISTRY.weights at module
 * load. Drives the bold / italic toggle availability in FieldEditor and any
 * future template-side helpers. Underline is pure CSS so it's universally
 * available and not represented here.
 *
 * Bold availability rule: family must ship a weight ≥ 600 (we treat 600/semi-
 * bold as "close enough to bold" for the toggle since most users perceive it
 * as bold; if a font has only 700+ that's also fine). Without a real bold face
 * we'd browser-fake the bold (synthetic bolding squishes glyphs and looks bad
 * in motion graphics — the entire reason we ship local woff2 in the first
 * place).
 *
 * Italic availability: today NO font in FONT_REGISTRY ships an italic face,
 * so this is universally false. The capability check is wired up so dropping
 * `400-italic.woff2` (and registering it in FONT_REGISTRY) flips the toggle on
 * for that family without a code change.
 *
 * `boldWeight` is the actual face weight to use when `bold: true` — we prefer
 * 700 if available, else fall back to the heaviest weight ≥ 600. null when no
 * bold-ish weight is loaded.
 */
export interface FontCapabilities {
  bold: boolean;
  italic: boolean;
  /** CSS font-weight value to use when bold is on. Falls back to 400 when bold not available. */
  boldWeight: number;
}

/**
 * One embeddable face source. `url` is either a PUBLIC static path (registry
 * woff2 under `/fonts/<slug>/<weight>.woff2`) or a tenant asset URL
 * (`/assets/<id>/file`) that requires the Authorization header — the `authed`
 * flag tells the fetcher (svgFontEmbed) which one it's dealing with.
 */
export interface FontFaceSource {
  weight: number;
  /** Public static path, or tenant asset URL relative to the API base. */
  url: string;
  /** True when fetching `url` needs the Bearer auth header (tenant asset). */
  authed: boolean;
  /** CSS font-style: "normal" | "italic". */
  style: string;
  /** CSS `format()` keyword: "woff2" | "woff" | "truetype" | "opentype". */
  format: string;
}

/** Unified descriptor returned by getFontFaceInfo — registry OR tenant. */
export interface FontFaceInfo {
  family: string;
  faces: FontFaceSource[];
}

/**
 * Tenant-font resolver injected by the app layer (src/lib/tenantFonts.ts) at
 * module load. Kept as an injected hook rather than a static import so the
 * Remotion render bundle (which imports THIS module but never the auth store /
 * API client) stays free of app-only dependencies. Null in the render worker
 * and until the app registers it; getFontFaceInfo / getFontCapabilities fall
 * back to the registry-only behavior in that case.
 */
type TenantFaceResolver = (family: string) => FontFaceInfo | undefined;
let tenantFaceResolver: TenantFaceResolver | null = null;
export function registerTenantFaceResolver(resolver: TenantFaceResolver): void {
  tenantFaceResolver = resolver;
}

/**
 * Look up format capabilities for a font family. Falls back to a "no-formats"
 * profile when the family isn't registered (e.g. the field value carries an
 * unknown family from an imported template). Tenant fonts resolve via the
 * injected resolver — their "available weights" are exactly the weights that
 * were uploaded, so bold is enabled only when a >=600 face exists.
 */
export function getFontCapabilities(family: string): FontCapabilities {
  const entry = FONT_REGISTRY.find((f) => f.family === family);
  const weights: number[] = entry
    ? entry.weights
    : (tenantFaceResolver?.(family)?.faces.map((f) => f.weight) ?? []);
  if (weights.length === 0) return { bold: false, italic: false, boldWeight: 400 };

  // Pick the bold face: prefer 700, else 800/900, else 600.
  const boldFace =
    weights.find((w) => w === 700) ??
    weights.find((w) => w >= 700) ??
    weights.find((w) => w >= 600);

  return {
    bold: boldFace != null,
    // No italic faces shipped today — see FONT_REGISTRY note above.
    italic: false,
    boldWeight: boldFace ?? 400,
  };
}

export const SUPPORTED_FONTS: SupportedFont[] = [
  // Sans-serif (body / UI)
  {
    name: "Rubik",
    family: "Rubik",
    css: "'Rubik', system-ui, -apple-system, sans-serif",
    category: "sans",
  },
  {
    name: "Inter",
    family: "Inter",
    css: "'Inter', system-ui, -apple-system, sans-serif",
    category: "sans",
  },
  {
    name: "Roboto",
    family: "Roboto",
    css: "'Roboto', system-ui, -apple-system, sans-serif",
    category: "sans",
  },
  {
    name: "Open Sans",
    family: "Open Sans",
    css: "'Open Sans', system-ui, -apple-system, sans-serif",
    category: "sans",
  },
  {
    name: "Montserrat",
    family: "Montserrat",
    css: "'Montserrat', system-ui, -apple-system, sans-serif",
    category: "sans",
  },
  {
    name: "Poppins",
    family: "Poppins",
    css: "'Poppins', system-ui, -apple-system, sans-serif",
    category: "sans",
  },
  {
    name: "Lato",
    family: "Lato",
    css: "'Lato', system-ui, -apple-system, sans-serif",
    category: "sans",
  },
  {
    name: "Raleway",
    family: "Raleway",
    css: "'Raleway', system-ui, -apple-system, sans-serif",
    category: "sans",
  },

  // Display (headlines, title cards)
  {
    name: "Oswald",
    family: "Oswald",
    css: "'Oswald', 'Impact', system-ui, sans-serif",
    category: "display",
  },
  {
    name: "Bebas Neue",
    family: "Bebas Neue",
    css: "'Bebas Neue', 'Impact', system-ui, sans-serif",
    category: "display",
  },

  // Monospace (tech/code aesthetics)
  {
    name: "Geist Mono",
    family: "Geist",
    css: "'Geist', ui-monospace, SFMono-Regular, monospace",
    category: "mono",
  },
  {
    name: "JetBrains Mono",
    family: "JetBrains Mono",
    css: "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace",
    category: "mono",
  },
  {
    name: "Fira Code",
    family: "Fira Code",
    css: "'Fira Code', ui-monospace, SFMono-Regular, monospace",
    category: "mono",
  },
  {
    name: "IBM Plex Mono",
    family: "IBM Plex Mono",
    css: "'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace",
    category: "mono",
  },
  {
    name: "Fragment Mono",
    family: "Fragment Mono",
    css: "'Fragment Mono', ui-monospace, SFMono-Regular, monospace",
    category: "mono",
  },

  // Serif (editorial, cinematic)
  {
    name: "Playfair Display",
    family: "Playfair Display",
    css: "'Playfair Display', 'Georgia', serif",
    category: "serif",
  },
  {
    name: "Merriweather",
    family: "Merriweather",
    css: "'Merriweather', 'Georgia', serif",
    category: "serif",
  },
];

/**
 * Font file registry. `family` is the CSS font-family name we register under;
 * `slug` is the folder under public/fonts; `weights` are the face weights
 * shipped for this family (each must have a corresponding woff2 at
 * public/fonts/<slug>/<weight>.woff2).
 */
interface FontEntry {
  family: string;
  slug: string;
  weights: number[];
}

/**
 * Look up a family's embeddable faces. Used by svgFontEmbed.ts to fetch the
 * bytes for inline @font-face embedding. Registry families resolve to their
 * public woff2 static paths; unknown families fall through to the tenant
 * resolver, whose faces carry authed asset URLs. Returns undefined when the
 * family is neither a registry font nor a known tenant font.
 */
export function getFontFaceInfo(family: string): FontFaceInfo | undefined {
  const entry = FONT_REGISTRY.find((f) => f.family === family);
  if (entry) {
    return {
      family,
      faces: entry.weights.map((weight) => ({
        weight,
        url: `/fonts/${entry.slug}/${weight}.woff2`,
        authed: false,
        style: "normal",
        format: "woff2",
      })),
    };
  }
  return tenantFaceResolver?.(family);
}

const FONT_REGISTRY: FontEntry[] = [
  // Sans
  { family: "Rubik", slug: "rubik", weights: [400, 500, 600, 700, 900] },
  { family: "Inter", slug: "inter", weights: [400, 500, 600, 700] },
  { family: "Roboto", slug: "roboto", weights: [400, 500, 700] },
  { family: "Open Sans", slug: "open-sans", weights: [400, 600, 700] },
  { family: "Montserrat", slug: "montserrat", weights: [400, 500, 600, 700] },
  { family: "Poppins", slug: "poppins", weights: [400, 500, 600, 700] },
  { family: "Lato", slug: "lato", weights: [400, 700] },
  { family: "Raleway", slug: "raleway", weights: [400, 500, 700] },

  // Display
  { family: "Oswald", slug: "oswald", weights: [400, 500, 700] },
  { family: "Bebas Neue", slug: "bebas-neue", weights: [400] },

  // Mono
  { family: "Geist", slug: "geist", weights: [400, 500, 700] },
  {
    family: "JetBrains Mono",
    slug: "jetbrains-mono",
    weights: [400, 500, 700],
  },
  { family: "Fira Code", slug: "fira-code", weights: [400, 500, 700] },
  { family: "IBM Plex Mono", slug: "ibm-plex-mono", weights: [400, 500, 700] },
  { family: "Fragment Mono", slug: "fragment-mono", weights: [400] },

  // Serif
  {
    family: "Playfair Display",
    slug: "playfair-display",
    weights: [400, 600, 700, 900],
  },
  { family: "Merriweather", slug: "merriweather", weights: [400, 700] },
];

// Cache the load promise on `window` rather than at module scope. Remotion's
// render bundle is evaluated more than once per tab in some contexts (warm-up
// vs render, nested frame contexts, etc.) — a module-scoped cache resets to
// null on each eval, so the second call opens a SECOND delayRender that nobody
// closes. Diagnostics confirmed this in 2026-05: Tab 0 logged two
// `[fonts] loadAllFonts start` cycles, both cleanly DONE, but a stuck handle
// from the second cycle aged from 5s → 88s and crashed the render.
//
// Window-scoping ensures: first eval opens delayRender + caches promise on
// window; any later eval (same tab) sees the cached promise on the SAME
// window and returns it without opening a second handle.
declare global {
  interface Window {
    __motionforgeFontLoadPromise?: Promise<void>;
  }
}

let loadPromise: Promise<void> | null =
  (typeof window !== "undefined" &&
    window.__motionforgeFontLoadPromise) ||
  null;

/**
 * Walk an arbitrary value (typically the parsed props.json tree) and collect
 * every font-family name referenced by a RichTextValue-shaped object.
 *
 * A RichTextValue (see src/types/template.ts) has the shape
 * `{ text: string; font: string; fontSize: number; ... }`. We don't have the
 * field schema available at render time (the worker only ships props.json,
 * not the schema), so we shape-match: any object that has a string `font` and
 * the other two RichText markers is treated as a typography value, and its
 * `font` is collected.
 *
 * We deliberately do NOT try to infer the weight from the value — UserComponent
 * TSX picks weights ad-hoc (typically 400 default, 700 if `bold`, sometimes
 * 500/600 by author taste) and we can't know which weights it'll actually use
 * without parsing the TSX. Loading every weight of a referenced family is
 * still the right tradeoff: families ship 1-5 weights (avg ~3), so per-template
 * scope drops from 51 woff2 to typically 3-15 — well under Chromium's per-host
 * connection cap × delayRender's 88s ceiling.
 *
 * Returns the set of family names to load. Empty set means "load nothing"
 * (caller should still always include the registry's first family as a safety
 * net so any unguarded TSX text gets a reasonable fallback).
 */
export function collectFontFamiliesFromProps(value: unknown): Set<string> {
  const found = new Set<string>();
  const walk = (v: unknown): void => {
    if (v === null || v === undefined) return;
    if (typeof v !== "object") return;
    if (Array.isArray(v)) {
      for (const item of v) walk(item);
      return;
    }
    const obj = v as Record<string, unknown>;
    // Shape-match: RichTextValue has all three of text, font, fontSize.
    if (
      typeof obj.text === "string" &&
      typeof obj.font === "string" &&
      typeof obj.fontSize === "number"
    ) {
      found.add(obj.font);
    }
    for (const child of Object.values(obj)) walk(child);
  };
  walk(value);
  return found;
}

/**
 * Load fonts from the registry.
 *
 * If `neededFamilies` is provided, only families whose `family` matches an
 * entry in the set are loaded (all weights of each matched family are loaded —
 * see collectFontFamiliesFromProps for why we don't filter by weight).
 *
 * If `neededFamilies` is omitted or empty, every (family, weight) in the
 * registry is loaded — this is the right default for the preview app, where
 * the user may edit any field at any time. The render path passes a narrow
 * set computed from the props.json the worker shipped, dropping per-tab
 * font fetches from 51 to typically 3-15.
 *
 * For each entry we build a FontFace pointing at the woff2 under
 * public/fonts/<slug>/<weight>.woff2 (path resolved via staticFile() so it
 * works in both Vite preview and Remotion render bundle), then add it to
 * document.fonts and await .load().
 *
 * Wraps the entire wait in delayRender/continueRender so the Remotion renderer
 * blocks frame capture until all fonts are registered and the FontFaceSet is
 * ready. This is what guarantees the renderer doesn't catch a frame mid-load
 * with system-font fallbacks.
 *
 * Idempotent — safe to call on every mount.
 */
export function loadAllFonts(neededFamilies?: Set<string>): Promise<void> {
  if (loadPromise) {
    console.log("[fonts] loadAllFonts cache hit — reusing existing promise");
    return loadPromise;
  }

  if (typeof document === "undefined" || !("fonts" in document)) {
    loadPromise = Promise.resolve();
    if (typeof window !== "undefined") {
      window.__motionforgeFontLoadPromise = loadPromise;
    }
    return loadPromise;
  }

  // Bump the render-side timeout from the default 28s to 90s. We're
  // loading woff2 faces against the bundle's local static server, and
  // when Remotion spawns a new render tab mid-stream the existing tabs are
  // still serving frames — the new tab's burst of concurrent fetches can
  // queue behind Chromium's 6-connections-per-host limit and stretch past
  // 28s under load. 90s is a generous ceiling that still aborts a
  // genuinely stuck render rather than hanging the worker forever. With
  // the per-template font loading above this is now massive headroom (a
  // typical render only fetches ~3-15 faces) rather than a tight ceiling.
  // NB: Remotion subtracts a 2s safety buffer before reporting a timeout,
  // so the error message will say "88000ms" not "90000ms" — that's the
  // documented behavior, not a config mismatch.
  const handle = delayRender("Loading MotionForge fonts", {
    timeoutInMilliseconds: 90_000,
  });

  const useFilter = neededFamilies !== undefined && neededFamilies.size > 0;
  const filterDescription = useFilter
    ? `[${[...neededFamilies!].join(", ")}]`
    : "(all families — no filter)";

  // Build the list of (family, weight, url) tuples to load. When a filter is
  // provided, families NOT in the set are skipped entirely. Weights inside a
  // matched family are all loaded — see collectFontFamiliesFromProps.
  type FontTask = { family: string; weight: number; url: string };
  const queue: FontTask[] = [];
  for (const { family, slug, weights } of FONT_REGISTRY) {
    if (useFilter && !neededFamilies!.has(family)) continue;
    for (const weight of weights) {
      queue.push({
        family,
        weight,
        url: staticFile(`fonts/${slug}/${weight}.woff2`),
      });
    }
  }

  let okCount = 0;
  let failCount = 0;
  console.log(
    `[fonts] loadAllFonts start, queue=${queue.length}, filter=${filterDescription}`,
  );

  const loadOne = async (t: FontTask): Promise<void> => {
    const face = new FontFace(t.family, `url("${t.url}") format("woff2")`, {
      weight: String(t.weight),
      style: "normal",
      display: "block",
    });
    document.fonts.add(face);
    try {
      await face.load();
      okCount++;
      console.log(`[fonts] OK ${t.family} ${t.weight}`);
    } catch (err) {
      failCount++;
      console.error(
        `[fonts] FAIL ${t.family} ${t.weight} (${t.url}):`,
        (err as { message?: string })?.message ?? err,
      );
    }
  };

  const CONCURRENCY = 6;
  const runWorker = async (): Promise<void> => {
    while (queue.length > 0) {
      const t = queue.shift();
      if (!t) return;
      await loadOne(t);
    }
  };

  // Direct cleanup — bypass Remotion's continueRender entirely.
  //
  // Found by reading remotion/dist/cjs/delay-render.js:
  //   1. Module init UNCONDITIONALLY resets `window.remotion_delayRenderHandles = []`
  //      (but preserves remotion_delayRenderTimeouts).
  //   2. continueRender(handle) finds handles via .filter() on the handles ARRAY.
  //      If the array was reset, the filter finds nothing, the inner if-block
  //      never runs, clearTimeout(map[handle].timeout) is never called.
  //   3. The setTimeout that fires the 88s "not cleared" fatal error LIVES IN
  //      THE MAP, not the array. So when the map's entry persists with a live
  //      timeout, the render dies even though continueRender returned cleanly.
  //
  // Webpack chunk re-init in render bundles triggers exactly this — the Remotion
  // module re-evaluates, handles array resets, our continueRender(handle) call
  // becomes a silent no-op, and the timer keeps ticking.
  //
  // Fix: walk the timeouts map directly, clearTimeout each "Loading MotionForge
  // fonts" entry's timer, and delete it from the map. This is what continueRender
  // would have done if the handles array hadn't been reset out from under it.
  const sweepOrphans = (): void => {
    if (typeof window === "undefined") return;
    const map = (window as unknown as {
      remotion_delayRenderTimeouts?: Record<
        string,
        { label: string | null; timeout?: ReturnType<typeof setTimeout> }
      >;
    }).remotion_delayRenderTimeouts;
    if (!map) return;
    let cleared = 0;
    for (const [id, meta] of Object.entries(map)) {
      if (meta?.label === "Loading MotionForge fonts") {
        if (meta.timeout) clearTimeout(meta.timeout);
        delete map[id];
        cleared++;
      }
    }
    if (cleared > 0) {
      console.log(
        `[fonts] swept ${cleared} orphan font handle(s) — bypassed continueRender to bust the 88s timer`,
      );
    }
  };

  loadPromise = Promise.all(
    Array.from({ length: CONCURRENCY }, () => runWorker()),
  )
    .then(() => document.fonts.ready)
    .then(() => {
      const rubikOk = document.fonts.check("500 16px Rubik");
      console.log(
        `[fonts] DONE ok=${okCount} fail=${failCount} size=${document.fonts.size} rubik500_available=${rubikOk}`,
      );
      continueRender(handle);
      sweepOrphans();
    })
    .catch((err) => {
      console.error("[fonts] loadAllFonts failed:", err);
      continueRender(handle);
      sweepOrphans();
    });

  if (typeof window !== "undefined") {
    window.__motionforgeFontLoadPromise = loadPromise;
  }
  return loadPromise;
}
